// src/pages/RideBookings.tsx
// Driver-side ride management — matches TripTracking UI language
// Reskinned into the Heartlas cream/gold/crimson palette (same family as
// DriverDashboard + DriverCars). Fonts/weights are unchanged (Syne + DM Sans).
// • Sized to sit inside the dashboard's content panel — rounded card, not a
//   full-viewport takeover — instead of hijacking html/body.
// • Header no longer carries its own "XAFARI" logo/title block; that lives
//   one level up in the dashboard shell.
// • Car marker — no more hardcoded ridecar.png. It now shows the image
//   configured on the driver's active (isSelected) vehicle's ride type
//   (same OperatorCategories `.image` field DriverCars' RowThumb reads),
//   and falls back to a drawn circle-badge with a simple car glyph when no
//   type image is set — the same photo → type-image → icon ladder used in
//   DriverCars, just without the "uploaded photo" tier (a top-down type
//   icon is what actually makes sense to rotate on a map).
// • Rider name/photo from users/{riderId} + auth/{riderId}
// • 2-minute request timer → auto-expire, grey out buttons, warning modal, call autoMatcher
// • Full-bleed map + frosted sidebar (desktop) / full-panel active view on
//   mobile — same layout language as the Requests panel, instead of the old
//   draggable peek/half/full bottom sheet.

"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  collection, doc, onSnapshot, query,
  where, getDocs, limit, getDoc,
} from "firebase/firestore";
import { ref as dbRef, onValue, off, remove } from "firebase/database";
import { db, rtdb } from "../../../firebase";
import type { RootState } from "../../../redux/store";
import {
  Car, Navigation, Phone, MessageSquare,
  CheckCircle, XCircle, Clock, Users, Wallet, MapPin,
  Loader2, Star, TrendingUp, Bell, Zap,
  ChevronDown, ChevronUp, AlertTriangle,
} from "lucide-react";
import {
  RIDE_TYPES,
} from "../../../CATEGORIES/OperatorCategories";
import { selectListings } from "../../../redux/fleetSlice";
import appLogo from "../../../assets/app/logo.png";

/* ─── Heartlas design tokens (same family as DriverDashboard / DriverCars) ── */
const H = {
  bg:        "#FBF6EE",
  panel:     "#FFFFFF",
  panelSoft: "#FCEFD4",
  dark:      "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",
  red:       "#C8202E",
  redDark:   "#9E1521",
  redSoft:   "#FBE7E9",
  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",
  green:     "#1DB954",
  greenDark: "#159C46",
  greenSoft: "#E5F8ED",
  amber:     "#D4900F",
  amberSoft: "#FCEFD4",
  blue:      "#3D7BD9",
  blueSoft:  "#EAF1FC",
  border:    "#EEDFC4",
};

/* ─── Vehicle types — same lookup DriverCars' RowThumb uses ─────────────── */
/* ─── Ride-type lookup — RIDE_TYPES carries the top-level image (car.png,
   bike.png, etc.), same image-first/icon-fallback pattern FactChip uses in
   ShortAccommodationDetails. ─────────────────────────────────────────── */
function rideTypeItem(id: any): any {
  return RIDE_TYPES.find((r: any) => r.id === id);
}

/* ─── Geo helpers ──────────────────────────────────────────────────────── */
function haversineKm(la1: number, lo1: number, la2: number, lo2: number) {
  const R = 6371, dLa = (la2 - la1) * Math.PI / 180, dLo = (lo2 - lo1) * Math.PI / 180;
  const a = Math.sin(dLa / 2) ** 2 + Math.cos(la1 * Math.PI / 180) * Math.cos(la2 * Math.PI / 180) * Math.sin(dLo / 2) ** 2;
  return +(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(2);
}
function bearingDeg(la1: number, lo1: number, la2: number, lo2: number): number {
  const f1 = la1 * Math.PI / 180, f2 = la2 * Math.PI / 180, dl = (lo2 - lo1) * Math.PI / 180;
  return ((Math.atan2(Math.sin(dl) * Math.cos(f2), Math.cos(f1) * Math.sin(f2) - Math.sin(f1) * Math.cos(f2) * Math.cos(dl)) * 180 / Math.PI) + 360) % 360;
}
function getInitials(name: string): string {
  if (!name) return "R";
  const p = name.trim().split(" ");
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : p[0][0].toUpperCase();
}

/* ─── Car icon ────────────────────────────────────────────────────────────
   Priority: the active vehicle's ride-type image (a designed top-down icon,
   same field DriverCars' RowThumb reads via `vehicleTypeItem(...)?.image`)
   → if none is configured, or the image fails to load, a drawn circle
   badge with a simple car glyph (Heartlas gold→red gradient), matching the
   "circle logo" fallback DriverCars uses for its thumbnails.
   Cached per (imgUrl, 5°-heading-bucket) — canvas drawn at most 72 times
   per distinct vehicle image, or once for the fallback. ───────────────── */
const carCache = new Map<string, string>();
function buildCarIcon(headingDeg: number, imgUrl: string | null, size = 96): Promise<string> {
  const r5 = Math.round(headingDeg / 5) * 5;
  const cacheKey = `${imgUrl || "fallback"}|${r5}`;
  if (carCache.has(cacheKey)) return Promise.resolve(carCache.get(cacheKey)!);

  const drawFallback = () => {
    const c = document.createElement("canvas");
    c.width = size; c.height = size;
    const ctx = c.getContext("2d")!;
    const grad = ctx.createLinearGradient(0, 0, size, size);
    grad.addColorStop(0, H.gold);
    grad.addColorStop(1, H.red);
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 3, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#fff";
    ctx.stroke();
    // simple car glyph — default icon when no ride-type image is set
    ctx.fillStyle = "#fff";
    const cx = size / 2, cy = size / 2;
    ctx.fillRect(cx - 16, cy - 6, 32, 13);
    ctx.beginPath();
    ctx.moveTo(cx - 9, cy - 6);
    ctx.lineTo(cx - 5, cy - 15);
    ctx.lineTo(cx + 5, cy - 15);
    ctx.lineTo(cx + 9, cy - 6);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = H.dark;
    ctx.beginPath(); ctx.arc(cx - 9, cy + 8, 4, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(cx + 9, cy + 8, 4, 0, Math.PI * 2); ctx.fill();
    const url = c.toDataURL("image/png");
    carCache.set(cacheKey, url);
    return url;
  };

  if (!imgUrl) return Promise.resolve(drawFallback());

  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = size; c.height = size;
      const ctx = c.getContext("2d")!;
      ctx.translate(size / 2, size / 2);
      // Type icons face south (180°) same as the old ridecar.png. Rotate by
      // (H − 180)° to face heading H.
      ctx.rotate(((r5 - 180) * Math.PI) / 180);
      ctx.drawImage(img, -size / 2, -size / 2, size, size);
      const url = c.toDataURL("image/png");
      carCache.set(cacheKey, url);
      resolve(url);
    };
    img.onerror = () => resolve(drawFallback());
    img.src = imgUrl;
  });
}

const mkPickup  = () => "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28'><circle cx='14' cy='14' r='11' fill='%231B0E06' stroke='white' stroke-width='3'/><circle cx='14' cy='14' r='4.5' fill='white'/></svg>`);
const mkDropoff = () => "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='34' height='44' viewBox='0 0 34 44'><path d='M17 0C7.6 0 0 7.6 0 17c0 14 17 27 17 27S34 31 34 17C34 7.6 26.4 0 17 0z' fill='%23C8202E'/><circle cx='17' cy='17' r='8.5' fill='white'/><circle cx='17' cy='17' r='4' fill='%23C8202E'/></svg>`);

/* ─── Map style — warm cream tint to match the dashboard ────────────────── */
const LIGHT_MAP = [
  { elementType: "geometry",            stylers: [{ color: "#F7F0E1" }] },
  { elementType: "labels.text.stroke",  stylers: [{ color: "#F7F0E1" }] },
  { elementType: "labels.text.fill",    stylers: [{ color: "#A6947F" }] },
  { featureType: "road",         elementType: "geometry",        stylers: [{ color: "#ffffff" }] },
  { featureType: "road",         elementType: "geometry.stroke", stylers: [{ color: "#EEDFC4" }] },
  { featureType: "road.highway", elementType: "geometry",        stylers: [{ color: "#FCEFD4" }] },
  { featureType: "water",        elementType: "geometry",        stylers: [{ color: "#CDE7F0" }] },
  { featureType: "poi",    stylers: [{ visibility: "off" }] },
  { featureType: "transit",stylers: [{ visibility: "off" }] },
];

/* ─── Status config ─────────────────────────────────────────────────── */
const SC: Record<string, {
  label: string; headline: string; sub: string;
  color: string; bg: string; Icon: any;
  next?: string; nextLabel?: string; nextGrad?: string;
}> = {
  accepted:  { label: "Head to Pickup",    headline: "Drive to pickup point",      sub: "Follow the dashed gold route to your rider.",          color: H.amber, bg: H.amberSoft,  Icon: Navigation, next: "arriving", nextLabel: "I'M ARRIVING",   nextGrad: `linear-gradient(135deg,${H.gold},${H.goldDark})` },
  arriving:  { label: "At Pickup",         headline: "You've arrived at pickup",   sub: "Let your rider know — then start the trip when ready.", color: H.blue,  bg: H.blueSoft,   Icon: MapPin,     next: "ongoing",  nextLabel: "START RIDE",     nextGrad: `linear-gradient(135deg,${H.blue},#2E6BC4)` },
  ongoing:   { label: "Ride in Progress",  headline: "Trip underway",              sub: "Rider is in the car. Drive to destination.",            color: H.green, bg: H.greenSoft,  Icon: Zap,        next: "completed",nextLabel: "COMPLETE RIDE",  nextGrad: `linear-gradient(135deg,${H.green},${H.greenDark})` },
  completed: { label: "Trip Complete!",    headline: "Trip complete!",             sub: "Great work. Earnings added to your balance.",            color: H.green, bg: H.greenSoft,  Icon: CheckCircle },
  cancelled: { label: "Cancelled",         headline: "Ride cancelled",             sub: "The rider cancelled this trip.",                        color: H.red,   bg: H.redSoft,    Icon: XCircle     },
};

const ACTIVE_STATUSES = ["accepted", "arriving", "ongoing"];
const REQUEST_TIMEOUT = 120; // 2 minutes
const API = "https://europe-west3-xsafari.cloudfunctions.net";
const LERP = 30;

/* ─── CSS — Heartlas cream/gold/red theme. Scoped to .rb so it never fights
   the parent dashboard's own global styles (no html/body overrides). ────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
.rb, .rb *, .rb *::before, .rb *::after{box-sizing:border-box;}
.rb{
  --red:${H.red};--red2:${H.redDark};
  --blk:${H.dark};--wht:${H.panel};--off:${H.bg};--off2:${H.panelSoft};
  --bord:${H.border};--muted:${H.textFaint};--smoke:${H.textMuted};
  --grn:${H.green};--blu:${H.blue};--amb:${H.amber};--gld:${H.goldDark};
  --serif:'Syne',Georgia,serif;
  --sans:'DM Sans',-apple-system,sans-serif;
  --sr:28px;
  width:100%;height:min(78vh,760px);min-height:520px;
  font-family:var(--sans);color:var(--blk);
  display:flex;flex-direction:column;overflow:hidden;position:relative;
  border-radius:24px;border:1px solid var(--bord);
  box-shadow:0 10px 34px rgba(27,14,6,.10);
  background:var(--off);
}
.rb button{font-family:inherit;}

/* ── Header ── */
.rb-hdr{position:absolute;top:0;left:0;right:0;z-index:60;height:56px;background:rgba(255,255,255,.9);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border-bottom:1px solid var(--bord);display:flex;align-items:center;gap:10px;padding:0 16px;}
.rb-hdr-title{font-family:var(--serif);font-size:14px;font-weight:800;letter-spacing:.3px;color:var(--blk);flex:1;}
.rb-online-pill{display:flex;align-items:center;gap:5px;background:${H.greenSoft};border:1px solid #BEE7CC;padding:4px 10px;border-radius:20px;}
.rb-online-dot{width:6px;height:6px;border-radius:50%;background:var(--grn);animation:rbBlink 1.4s ease-in-out infinite;}
.rb-online-txt{font-size:9px;font-weight:700;letter-spacing:2px;color:#15803D;}
.rb-earn-pill{display:flex;align-items:center;gap:5px;border:1px solid var(--bord);padding:4px 10px;border-radius:20px;background:var(--wht);}
.rb-earn-val{font-family:var(--serif);font-size:13px;color:var(--gld);font-weight:700;}

/* ── Tabs ── */
.rb-tabbar{position:absolute;top:56px;left:0;right:0;z-index:55;display:flex;background:rgba(255,255,255,.92);backdrop-filter:blur(16px);border-bottom:1px solid var(--bord);}
.rb-tab{flex:1;display:flex;align-items:center;justify-content:center;gap:6px;padding:11px 0;cursor:pointer;background:none;border:none;border-bottom:3px solid transparent;font-family:var(--sans);color:var(--muted);font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;transition:color .2s,border-color .2s;margin-bottom:-1px;}
.rb-tab:hover{color:var(--smoke);}
.rb-tab-on{color:var(--blk);border-bottom-color:var(--red);}
.rb-tab-pip{width:6px;height:6px;border-radius:50%;background:var(--red);animation:rbBlink 1.4s infinite;}

/* ── Map ── */
#rb-gmap{position:absolute;inset:0;width:100%;height:100%;}

/* ── Desktop sidebar ── */
@media(min-width:900px){
  .rb-active-panel{display:none!important;}
  .rb-sidebar{position:absolute;right:0;top:96px;bottom:0;width:380px;z-index:30;background:var(--wht);border-left:1px solid var(--bord);display:flex;flex-direction:column;overflow:hidden;box-shadow:-6px 0 28px rgba(27,14,6,.06);}
  .rb-sb-status{flex-shrink:0;padding:18px 20px 14px;border-bottom:1px solid var(--off2);}
  .rb-sb-status-row{display:flex;align-items:center;gap:12px;}
  .rb-sb-icon{width:46px;height:46px;border-radius:14px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .rb-sb-body{flex:1;overflow-y:auto;padding:14px 20px 40px;}
  .rb-sb-body::-webkit-scrollbar{width:0;}
  .rb-req-panel{position:absolute;right:0;top:96px;bottom:0;width:380px;z-index:30;background:var(--off);border-left:1px solid var(--bord);overflow-y:auto;}
  .rb-req-panel::-webkit-scrollbar{width:0;}
}
@media(max-width:899px){
  .rb-sidebar{display:none!important;}
  .rb-req-panel{position:absolute;inset:96px 0 0 0;z-index:30;background:var(--off);overflow-y:auto;}
  .rb-req-panel::-webkit-scrollbar{width:0;}
  /* Active tab on mobile — same full-panel layout as Requests, instead of
     a draggable peek/half/full sheet. */
  .rb-active-panel{position:absolute;inset:96px 0 0 0;z-index:30;background:var(--off);overflow-y:auto;}
  .rb-active-panel::-webkit-scrollbar{width:0;}
}

/* ── Idle state ── */
.rb-idle{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:32px 24px;gap:14px;text-align:center;}
.rb-idle-orb{width:72px;height:72px;border-radius:50%;background:linear-gradient(135deg,var(--off),#fff);border:1.5px solid var(--bord);display:flex;align-items:center;justify-content:center;color:var(--muted);box-shadow:0 4px 20px rgba(27,14,6,.06);position:relative;overflow:hidden;}
.rb-idle-ring{position:absolute;inset:-10px;border-radius:50%;border:1.5px dashed var(--bord);animation:rbSpin 12s linear infinite;}
.rb-idle-title{font-family:var(--serif);font-size:22px;font-weight:800;color:var(--blk);}
.rb-idle-sub{font-size:12px;color:var(--muted);line-height:1.7;max-width:220px;}
.rb-pulse-row{display:flex;gap:6px;}
.rb-pulse{width:7px;height:7px;border-radius:50%;background:var(--red);animation:rbBlink 1.5s ease-in-out infinite;}
.rb-pulse:nth-child(2){animation-delay:.25s;}.rb-pulse:nth-child(3){animation-delay:.5s;}

/* ── Status banner ── */
.rb-status-banner{display:flex;align-items:center;gap:12px;padding:14px 15px;border-radius:16px;margin-bottom:14px;border:1px solid;box-shadow:0 2px 10px rgba(27,14,6,.05),inset 0 1px 0 rgba(255,255,255,.7);}
.rb-status-icon{width:44px;height:44px;border-radius:13px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.rb-status-text{flex:1;}
.rb-status-label{font-family:var(--serif);font-size:16px;font-weight:800;line-height:1.2;}
.rb-status-sub{font-size:10px;color:var(--smoke);margin-top:2px;line-height:1.5;}

/* ── Rider card (dark, warm ink) ── */
.rb-rider-card{background:linear-gradient(145deg,${H.dark},#2A1710);color:var(--wht);border-radius:18px;padding:16px;margin-bottom:14px;position:relative;overflow:hidden;box-shadow:0 4px 20px rgba(27,14,6,.22),inset 0 1px 0 rgba(255,255,255,.05);}
.rb-rider-card::before{content:'';position:absolute;top:-50px;right:-50px;width:150px;height:150px;border-radius:50%;background:rgba(242,169,62,.14);pointer-events:none;}
.rb-rc-row{display:flex;align-items:center;gap:12px;position:relative;}
.rb-rc-av-wrap{position:relative;flex-shrink:0;}
.rb-rc-av{width:52px;height:52px;border-radius:50%;border:2px solid rgba(255,255,255,.14);object-fit:cover;}
.rb-rc-av-fb{width:52px;height:52px;border-radius:50%;background:rgba(242,169,62,.18);border:2px solid rgba(242,169,62,.38);display:flex;align-items:center;justify-content:center;font-family:var(--serif);font-size:20px;font-weight:800;color:var(--wht);flex-shrink:0;}
.rb-rc-online{position:absolute;bottom:1px;right:1px;width:12px;height:12px;border-radius:50%;background:#4ade80;border:2px solid #2A1710;}
.rb-rc-info{flex:1;min-width:0;}
.rb-rc-name{font-family:var(--serif);font-size:16px;font-weight:800;color:var(--wht);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.rb-rc-rating{display:flex;align-items:center;gap:3px;color:${H.gold};font-size:10px;font-weight:700;margin:3px 0 2px;}
.rb-rc-sub{font-size:10px;color:rgba(255,255,255,.5);}
.rb-rc-acts{display:flex;gap:8px;margin-top:12px;position:relative;}
.rb-rc-act{flex:1;display:flex;align-items:center;justify-content:center;gap:6px;padding:10px 0;border-radius:11px;border:none;cursor:pointer;font-size:11px;font-weight:700;font-family:var(--sans);transition:all .15s;letter-spacing:.3px;}
.rb-rc-act.ph{background:rgba(255,255,255,.09);color:rgba(255,255,255,.85);}
.rb-rc-act.ph:hover{background:rgba(255,255,255,.15);transform:translateY(-1px);}
.rb-rc-act.ms{background:var(--red);color:#fff;}
.rb-rc-act.ms:hover{background:var(--red2);transform:translateY(-1px);box-shadow:0 4px 12px rgba(200,32,46,.4);}

/* ── Route card ── */
.rb-route{background:var(--off);border:1px solid var(--bord);border-radius:14px;padding:13px 15px;margin-bottom:14px;}
.rb-route-row{display:flex;align-items:flex-start;gap:10px;}
.rb-route-dots{display:flex;flex-direction:column;align-items:center;flex-shrink:0;padding-top:3px;}
.rb-route-dot{width:9px;height:9px;border-radius:50%;border:2px solid;}
.rb-route-vline{width:1px;height:18px;background:var(--bord);margin:3px 0;}
.rb-route-body{flex:1;min-width:0;}
.rb-route-lbl{font-size:8px;font-weight:700;letter-spacing:1.5px;color:var(--muted);text-transform:uppercase;margin-bottom:2px;}
.rb-route-addr{font-size:12px;font-weight:600;color:var(--blk);line-height:1.35;}
.rb-route-km{font-size:10px;color:var(--smoke);margin-top:2px;}

/* ── Fare card (driver sees their earning) ── */
.rb-fare{display:flex;align-items:center;gap:10px;background:${H.goldSoft};border:1px solid ${H.gold};border-radius:14px;padding:13px 15px;margin-bottom:14px;box-shadow:0 2px 8px rgba(212,144,15,.1),inset 0 1px 0 rgba(255,255,255,.8);}
.rb-fare-lbl{font-size:11px;font-weight:600;color:var(--smoke);flex:1;}
.rb-fare-amt{font-family:var(--serif);font-size:22px;font-weight:800;color:var(--gld);}

/* ── Advance button ── */
.rb-advance{width:100%;padding:14px 0;border:none;border-radius:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;font-size:10px;font-weight:700;letter-spacing:2px;color:#fff;font-family:var(--sans);transition:all .18s;margin-bottom:10px;box-shadow:0 4px 16px rgba(27,14,6,.13);}
.rb-advance:hover:not(:disabled){opacity:.9;transform:translateY(-1px);box-shadow:0 6px 20px rgba(27,14,6,.18);}
.rb-advance:disabled{opacity:.4;cursor:not-allowed;transform:none;}

/* ── Terminal ── */
.rb-terminal{display:flex;flex-direction:column;align-items:center;gap:12px;padding:16px 0 8px;text-align:center;}
.rb-terminal-icon{width:68px;height:68px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid;box-shadow:0 8px 28px rgba(27,14,6,.08);}
.rb-terminal-title{font-family:var(--serif);font-size:24px;font-weight:800;color:var(--blk);}
.rb-terminal-earn{font-size:13px;color:var(--smoke);line-height:1.6;}
.rb-done-btn{margin-top:8px;padding:12px 32px;border:none;border-radius:12px;background:var(--blk);color:#fff;font-size:10px;font-weight:700;letter-spacing:2px;cursor:pointer;font-family:var(--sans);transition:opacity .2s;box-shadow:0 4px 16px rgba(27,14,6,.15);}
.rb-done-btn:hover{opacity:.8;}

/* ─────────────────────────────────────
   REQUESTS / ACTIVE PANEL (shared inner layout)
───────────────────────────────────── */
.rb-req-inner{padding:16px 18px 80px;}
.rb-req-hdr{display:flex;align-items:flex-start;justify-content:space-between;padding:20px 0 16px;border-bottom:1.5px solid var(--blk);margin-bottom:16px;}
.rb-req-title{font-family:var(--serif);font-size:26px;font-weight:800;color:var(--blk);}
.rb-req-sub{font-size:11px;color:var(--muted);margin-top:3px;}
.rb-req-badge{display:flex;align-items:center;gap:5px;background:var(--red);color:#fff;border-radius:20px;padding:5px 12px;font-size:10px;font-weight:700;flex-shrink:0;}

/* ── Request card ── */
.req-card{background:var(--wht);border-radius:18px;margin-bottom:12px;overflow:hidden;border:1px solid var(--bord);box-shadow:0 2px 8px rgba(27,14,6,.04),0 8px 24px rgba(27,14,6,.06);opacity:0;transform:translateY(10px);animation:rbUp .3s ease var(--cd,0s) both;transition:box-shadow .18s,transform .18s;}
.req-card:hover{transform:translateY(-2px);box-shadow:0 4px 16px rgba(27,14,6,.07),0 12px 32px rgba(27,14,6,.07);}
.req-card.expired{opacity:.5;pointer-events:none;}
.req-accent{height:4px;}
.req-body{padding:14px 16px;}
.req-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;gap:8px;}
.req-pill{display:flex;align-items:center;gap:5px;padding:4px 10px;border-radius:20px;border:1.5px solid;font-size:9px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;}

/* ── Circular countdown timer ── */
.req-timer-wrap{position:relative;width:48px;height:48px;flex-shrink:0;}
.req-timer-svg{position:absolute;inset:0;transform:rotate(-90deg);}
.req-timer-bg{fill:none;stroke:var(--off2);stroke-width:3;}
.req-timer-fill{fill:none;stroke-width:3;stroke-linecap:round;transition:stroke-dashoffset 1s linear,stroke .5s;}
.req-timer-center{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;}
.req-timer-num{font-family:var(--serif);font-size:14px;font-weight:800;line-height:1;}
.req-timer-lbl{font-size:6px;font-weight:700;letter-spacing:1px;color:var(--muted);margin-top:0px;}

/* ── Rider info row ── */
.req-rider{display:flex;align-items:center;gap:10px;background:var(--off);border:1px solid var(--bord);border-radius:13px;padding:10px 12px;margin-bottom:11px;}
.req-av{width:40px;height:40px;border-radius:50%;object-fit:cover;border:2px solid rgba(255,255,255,.8);flex-shrink:0;}
.req-av-fb{width:40px;height:40px;border-radius:50%;background:linear-gradient(135deg,${H.goldSoft},${H.panelSoft});border:2px solid rgba(255,255,255,.8);display:flex;align-items:center;justify-content:center;font-family:var(--serif);font-size:16px;font-weight:800;color:var(--smoke);flex-shrink:0;}
.req-rider-info{flex:1;min-width:0;}
.req-rider-name{font-family:var(--serif);font-size:15px;font-weight:800;color:var(--blk);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.req-rider-rating{display:flex;align-items:center;gap:3px;color:var(--gld);font-size:9px;font-weight:700;margin:2px 0;}
.req-rider-sub{font-size:10px;color:var(--muted);}
.req-contacts{display:flex;gap:6px;flex-shrink:0;}
.req-cbtn{width:34px;height:34px;border-radius:10px;border:1px solid var(--bord);background:var(--wht);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .15s;color:var(--smoke);box-shadow:0 2px 6px rgba(27,14,6,.06);}
.req-cbtn.ph:hover{background:${H.greenSoft};border-color:#6ee7b7;color:#065f46;transform:scale(1.08);}
.req-cbtn.ms:hover{background:${H.blueSoft};border-color:#93c5fd;color:#1e40af;transform:scale(1.08);}

/* ── Route in card ── */
.req-route{background:var(--off);border:1px solid var(--bord);border-radius:11px;padding:10px 12px;margin-bottom:11px;}
.req-route-row{display:flex;align-items:flex-start;gap:9px;}
.req-route-row+.req-route-row{margin-top:7px;}
.req-route-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;margin-top:3px;}
.req-route-body{flex:1;min-width:0;}
.req-route-lbl{font-size:7px;font-weight:700;letter-spacing:1.5px;color:var(--muted);text-transform:uppercase;margin-bottom:1px;}
.req-route-addr{font-size:12px;font-weight:600;color:var(--blk);line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

/* ── Stats row ── */
.req-stats{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px;}
.req-stat{display:flex;align-items:center;gap:5px;background:var(--off);border:1px solid var(--bord);padding:5px 9px;border-radius:9px;font-size:11px;font-weight:600;color:var(--smoke);}

/* ── Accept / Decline ── */
.req-acts{display:flex;gap:8px;}
.req-dec{flex:1;display:flex;align-items:center;justify-content:center;gap:6px;background:none;border:1.5px solid var(--red);border-radius:12px;padding:11px 0;cursor:pointer;font-size:10px;font-weight:700;letter-spacing:1.5px;color:var(--red);font-family:var(--sans);transition:all .15s;}
.req-dec:hover:not(:disabled){background:${H.redSoft};transform:translateY(-1px);}
.req-dec:disabled{opacity:.4;cursor:not-allowed;transform:none;}
.req-acc{flex:2;display:flex;align-items:center;justify-content:center;gap:6px;border:none;border-radius:12px;padding:11px 0;cursor:pointer;font-size:10px;font-weight:700;letter-spacing:1.5px;color:#fff;font-family:var(--sans);background:linear-gradient(135deg,${H.green},${H.greenDark});box-shadow:0 4px 14px rgba(29,185,84,.3);transition:all .18s;}
.req-acc:hover:not(:disabled){opacity:.9;transform:translateY(-1px);box-shadow:0 6px 18px rgba(29,185,84,.38);}
.req-acc:disabled{opacity:.4;cursor:not-allowed;transform:none;box-shadow:none;}

/* ── Empty ── */
.rb-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:64px 32px;text-align:center;gap:12px;}
.rb-empty-title{font-family:var(--serif);font-size:26px;font-weight:800;color:var(--blk);}
.rb-empty-sub{font-size:12px;color:var(--muted);line-height:1.7;max-width:230px;}

/* ── Warning modal (request expired) ── */
.rb-modal-overlay{position:absolute;inset:0;z-index:200;background:rgba(27,14,6,.42);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;padding:24px;animation:rbFadeIn .2s ease;}
.rb-modal{background:var(--wht);border-radius:22px;padding:28px 24px;max-width:360px;width:100%;box-shadow:0 24px 80px rgba(27,14,6,.22);animation:rbSlideUp .3s cubic-bezier(.22,1,.36,1);}
.rb-modal-icon{width:60px;height:60px;border-radius:50%;background:${H.redSoft};border:1.5px solid #F3B9BE;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;color:var(--red);}
.rb-modal-title{font-family:var(--serif);font-size:22px;font-weight:800;color:var(--blk);text-align:center;margin-bottom:8px;}
.rb-modal-sub{font-size:13px;color:var(--smoke);text-align:center;line-height:1.6;margin-bottom:22px;}
.rb-modal-btn{width:100%;padding:14px 0;border:none;border-radius:13px;background:var(--blk);color:#fff;font-family:var(--sans);font-size:11px;font-weight:700;letter-spacing:2px;cursor:pointer;transition:opacity .2s;box-shadow:0 4px 14px rgba(27,14,6,.14);}
.rb-modal-btn:hover{opacity:.82;}

/* ── Toast ── */
.rb-toast{position:absolute;bottom:20px;left:50%;transform:translateX(-50%) translateY(20px);background:var(--blk);color:#fff;padding:12px 20px;display:flex;align-items:center;gap:10px;font-size:12.5px;font-weight:700;z-index:300;opacity:0;pointer-events:none;transition:opacity .38s,transform .38s;white-space:nowrap;border-radius:16px;box-shadow:0 8px 32px rgba(27,14,6,.25);border-left:4px solid var(--grn);}
.rb-toast.show{opacity:1;transform:translateX(-50%) translateY(0);}

/* ── Animations ── */
.spin{animation:rbSpin .8s linear infinite;}
@keyframes rbSpin   {to{transform:rotate(360deg);}}
@keyframes rbBlink  {0%,100%{opacity:.4;}50%{opacity:1;}}
@keyframes rbUp     {to{opacity:1;transform:translateY(0);}}
@keyframes rbFadeIn {from{opacity:0;}to{opacity:1;}}
@keyframes rbSlideUp{from{opacity:0;transform:translateY(24px);}to{opacity:1;transform:none;}}
.rb ::-webkit-scrollbar{width:0;height:0;}
`;

/* ═══════════════════════════════════════════════════════════════════════ */
export default function RideBookings() {
  const navigate = useNavigate();
  const { user }  = useSelector((s: RootState) => s.auth);
  const driverId  = (user as any)?.userId || (user as any)?.uid;

  /* ── Driver's active vehicle — the ONE listing that is both isSelected
     AND driverId === this device's authenticated user. Reads from fleetSlice,
     the same slice DriverDashboard/DriverCars populate via
     listenToDriverData(hostId). No "first listing" fallback — a fleet can
     have several drivers each with their own active vehicle, so this only
     ever shows the vehicle THIS driver personally selected. ── */
  const listings: any[] = useSelector(selectListings);
  const activeVehicle = useMemo(
    () => listings.find((l: any) => l.isSelected && l.driverId === driverId) || null,
    [listings, driverId]
  );
  // "Available" = the vehicle is both selected by this driver AND currently
  // online. The map marker only ever renders when this is true.
  const activeVehicleOnline = !!activeVehicle?.isOnline;
  const rideTypeImage = useMemo(
    () => rideTypeItem(activeVehicle?.rideType)?.image ?? null,
    [activeVehicle]
  );
  const vehicleImageUrl = activeVehicleOnline ? rideTypeImage : null;
  const vehicleImageUrlRef = useRef<string | null>(null);
  useEffect(() => { vehicleImageUrlRef.current = vehicleImageUrl; }, [vehicleImageUrl]);
  const vehicleAvailableRef = useRef<boolean>(false);
  useEffect(() => { vehicleAvailableRef.current = activeVehicleOnline; }, [activeVehicleOnline]);

  /* Map refs */
  const [mapReady,     setMapReady]    = useState(typeof (window as any).google !== "undefined");
  const mapRef         = useRef<any>(null);
  const carMarkerRef   = useRef<any>(null);
  const pickupMarkRef  = useRef<any>(null);
  const dropMarkRef    = useRef<any>(null);
  const polylinesRef   = useRef<any[]>([]);
  const lerpRef        = useRef<ReturnType<typeof setInterval> | null>(null);
  const displayPosRef  = useRef<{ lat: number; lng: number } | null>(null);
  const prevGPSRef     = useRef<{ lat: number; lng: number } | null>(null);
const headingRef        = useRef(0);
  const watchRef          = useRef<number | null>(null);
  const routePathRef      = useRef<any[]>([]);
  const carPctRef         = useRef<number>(0);
  const lastRouteKeyRef   = useRef<string>("");
  const dirRendererRef    = useRef<any>(null);

  /* State */
  const [tab,            setTab]           = useState<"active" | "requests">("active");
  const [sheetMode,      setSheetMode]     = useState<"peek" | "half" | "full">("peek");
  const [accepting,      setAccepting]     = useState<string | null>(null);
  const [declining,      setDeclining]     = useState<string | null>(null);
  const [advancing,      setAdvancing]     = useState(false);
  const [driverPos,      setDriverPos]     = useState<{ lat: number; lng: number } | null>(null);
  const [activeTrip,     setActiveTrip]    = useState<any>(null);
  const [sessionEarns,   setSessionEarns]  = useState(0);
  const [showToast,      setShowToast]     = useState(false);
  const [toastMsg,       setToastMsg]      = useState("");
  const [initializing,   setInitializing]  = useState(true);
  const [requestedTrips, setRequestedTrips]= useState<any[]>([]);
  const [countdowns,     setCountdowns]    = useState<Record<string, number>>({});
  const [expiredIds,     setExpiredIds]    = useState<Set<string>>(new Set());
  const [riderCache,     setRiderCache]    = useState<Record<string, any>>({});
  const [expiredModal,   setExpiredModal]  = useState<{ tripId: string; riderName: string } | null>(null);

  const activeTripRef      = useRef<any>(null);
  const activeTripUnsubRef = useRef<(() => void) | null>(null);
  const expiredIdsRef      = useRef<Set<string>>(new Set());

  useEffect(() => { activeTripRef.current = activeTrip; }, [activeTrip]);
  useEffect(() => { expiredIdsRef.current = expiredIds; }, [expiredIds]);

  const toast = (msg: string) => {
    setToastMsg(msg); setShowToast(true);
    setTimeout(() => setShowToast(false), 3500);
  };

  /* CSS injection */
  useEffect(() => {
    const id = "rb-css-heartlas";
    if (!document.getElementById(id)) {
      const t = document.createElement("style"); t.id = id; t.textContent = CSS;
      document.head.appendChild(t);
    }
  }, []);

  /* Google Maps ready */
  useEffect(() => {
    if (mapReady) return;
    const t = setInterval(() => {
      if (typeof (window as any).google !== "undefined") { setMapReady(true); clearInterval(t); }
    }, 100);
    return () => clearInterval(t);
  }, [mapReady]);

  /* Init map — world view, then GPS snaps it */
  useEffect(() => {
    if (!mapReady) return;
    const el = document.getElementById("rb-gmap");
    if (!el || mapRef.current) return;
    mapRef.current = new (window as any).google.maps.Map(el, {
      center: { lat: 0, lng: 20 }, zoom: 3,
      styles: LIGHT_MAP, disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: { position: (window as any).google.maps.ControlPosition.RIGHT_CENTER },
    });
  }, [mapReady]);

  /* ── Fetch rider info from users/{riderId} + auth/{riderId} ── */
  const fetchRiderInfo = useCallback(async (riderId: string) => {
    if (!riderId || riderCache[riderId]) return;
    try {
      const [uSnap, aSnap] = await Promise.all([
        getDoc(doc(db, "users",  riderId)),
        getDoc(doc(db, "auth",   riderId)),
      ]);
      const u = uSnap.exists() ? uSnap.data() as any : {};
      const a = aSnap.exists() ? aSnap.data() as any : {};

      // Build display name from users collection firstName
      const firstName = u.firstName || a.firstName || u.fullName?.split(" ")?.[0] || "Rider";
      const lastName  = u.lastName  || "";

      setRiderCache(prev => ({
        ...prev,
        [riderId]: {
          name:     firstName + (lastName ? " " + lastName : ""),
          initials: getInitials(firstName + (lastName ? " " + lastName : "")),
          photo:    u.profilePicture || u.profilePhoto || u.photoURL || null,
          rating:   u.rating ? Number(u.rating).toFixed(1) : null,
          phone:    a.contactNumber || u.contactNumber || u.phone || u.phoneNumber || null,
        },
      }));
    } catch (e) { console.warn("fetchRiderInfo:", e); }
  }, [riderCache]);

  /* ── Subscribe to active trip ── */
  const subscribeToTrip = useCallback((tripId: string) => {
    if (activeTripUnsubRef.current) { activeTripUnsubRef.current(); activeTripUnsubRef.current = null; }
    const unsub = onSnapshot(doc(db, "rideTrips", tripId), snap => {
      if (!snap.exists()) return;
      const data = { id: snap.id, ...snap.data() } as any;
      const prev = activeTripRef.current?.status;
      setActiveTrip(data); activeTripRef.current = data;
      if (data.riderId) fetchRiderInfo(data.riderId);
      if (ACTIVE_STATUSES.includes(data.status)) { setSheetMode("half"); setTab("active"); }
      if (data.status === "completed" && prev !== "completed") {
        setSessionEarns(e => e + (data.driverProfit || 0));
        setSheetMode("full");
        toast(`Trip complete! Earned EUR ${(data.driverProfit || 0).toFixed(2)}`);
      }
    });
    activeTripUnsubRef.current = unsub;
  }, [fetchRiderInfo]);

  /* ── Mount: restore active trip ── */
  useEffect(() => {
    if (!driverId) { setInitializing(false); return; }
    const run = async () => {
      try {
        const q = query(collection(db, "rideTrips"), where("driverId", "==", driverId), where("status", "in", ACTIVE_STATUSES), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const data = { id: snap.docs[0].id, ...snap.docs[0].data() } as any;
          setActiveTrip(data); activeTripRef.current = data;
          setSheetMode("half");
          subscribeToTrip(data.tripId || data.id);
          if (data.riderId) fetchRiderInfo(data.riderId);
          setTab("active");
        } else {
          setSheetMode("peek");
        }
      } catch (e) { console.error(e); }
      finally { setInitializing(false); }
    };
    run();
    return () => { if (activeTripUnsubRef.current) activeTripUnsubRef.current(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  /* ── Subscribe to "requested" trips assigned to this driver ── */
  useEffect(() => {
    if (!driverId) return;
    const q = query(collection(db, "rideTrips"), where("driverId", "==", driverId), where("status", "==", "requested"));
    const unsub = onSnapshot(q, snap => {
      const trips = snap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      trips.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
      setRequestedTrips(trips);
      trips.forEach(t => { if (t.riderId) fetchRiderInfo(t.riderId); });
      // Init countdowns for new trips only
      setCountdowns(prev => {
        const next = { ...prev };
        trips.forEach(t => {
          const k = t.tripId || t.id;
          if (!(k in next) && !expiredIdsRef.current.has(k)) next[k] = REQUEST_TIMEOUT;
        });
        Object.keys(next).forEach(k => {
          if (!trips.find(t => (t.tripId || t.id) === k)) delete next[k];
        });
        return next;
      });
      if (trips.length > 0 && !activeTripRef.current) setTab("requests");
    });
    return () => unsub();
  }, [driverId, fetchRiderInfo]);

  /* ── RTDB wake-up for new request ── */
  useEffect(() => {
    if (!driverId) return;
    const r = dbRef(rtdb, `driverRequests/${driverId}`);
    const handler = onValue(r, snap => {
      if (activeTripRef.current && ACTIVE_STATUSES.includes(activeTripRef.current.status)) return;
      if (snap.exists()) setTab("requests");
    });
    return () => off(r, "value", handler);
  }, [driverId]);

  /* ── Countdown ticker + auto-expire at 0 ── */
 /* ── Countdown ticker + auto-expire at 0 ── */
useEffect(() => {
  const iv = setInterval(() => {
    setCountdowns(prev => {
      const next = { ...prev };
      let changed = false;
      Object.entries(next).forEach(([k, v]) => {
        if (v > 0) {
          next[k] = v - 1;
          changed = true;

          // At 1s remaining — call ignoreAndRematch immediately
          if (v === 1 && !expiredIdsRef.current.has(k)) {
            expiredIdsRef.current.add(k);
            setExpiredIds(new Set(expiredIdsRef.current));

            const trip = requestedTrips.find(t => (t.tripId || t.id) === k);
            const riderName = riderCache[trip?.riderId || ""]?.name || "Rider";
            setExpiredModal({ tripId: k, riderName });

            // Remove RTDB node + call backend rematch
            if (driverId) {
              remove(dbRef(rtdb, `driverRequests/${driverId}/${k}`)).catch(console.warn);
              fetch(`${API}/ignoreAndRematch`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ tripId: k, ignoredDriverId: driverId }),
              }).catch(console.warn);
            }
          }
        }
      });
      return changed ? next : prev;
    });
  }, 1000);
  return () => clearInterval(iv);
}, [requestedTrips, riderCache, driverId]);

  /* ── Car animation ── */
  const animateCar = useCallback(async (lat: number, lng: number, heading: number) => {
    if (!mapRef.current || !mapReady) return;

    // Not this driver's own selected+online vehicle → show nothing on the
    // map, not even a fallback icon.
    if (!vehicleAvailableRef.current) {
      if (carMarkerRef.current) { carMarkerRef.current.setMap(null); carMarkerRef.current = null; }
      displayPosRef.current = null;
      return;
    }

    const g = (window as any).google.maps;
    const iconUrl = await buildCarIcon(heading, vehicleImageUrlRef.current, 64);
    if (!carMarkerRef.current) {
      carMarkerRef.current = new g.Marker({
  position: { lat, lng }, map: mapRef.current,
  icon: { url: iconUrl, scaledSize: new g.Size(96, 96), anchor: new g.Point(48, 48) },
  
  zIndex: 20, optimized: false,
      });
      displayPosRef.current = { lat, lng }; return;
    }
// Snap to road path if available
    const path = routePathRef.current;
    if (path.length > 1) {
      const dists: number[] = [0];
      for (let i = 1; i < path.length; i++)
        dists.push(dists[i - 1] + haversineKm(path[i-1].lat(), path[i-1].lng(), path[i].lat(), path[i].lng()));
      const totalKm = dists[dists.length - 1];
      let closestIdx = 0, closestDist = Infinity;
      for (let i = 0; i < path.length; i++) {
        const d = haversineKm(lat, lng, path[i].lat(), path[i].lng());
        if (d < closestDist) { closestDist = d; closestIdx = i; }
      }
      const gpsFrac = dists[closestIdx] / totalKm;
      if (gpsFrac > carPctRef.current) carPctRef.current = gpsFrac;
      const targetKm = Math.min(carPctRef.current, 1) * totalKm;
      let seg = 0;
      for (let i = 1; i < dists.length; i++) { if (dists[i] >= targetKm) { seg = i - 1; break; } seg = i - 1; }
      const p1 = path[seg], p2 = path[Math.min(seg + 1, path.length - 1)];
      const segLen = dists[seg + 1] - dists[seg];
      const frac   = segLen > 0 ? (targetKm - dists[seg]) / segLen : 0;
      const sLat   = p1.lat() + (p2.lat() - p1.lat()) * frac;
      const sLng   = p1.lng() + (p2.lng() - p1.lng()) * frac;
      const sHdg   = bearingDeg(p1.lat(), p1.lng(), p2.lat(), p2.lng());
      const snappedIcon = await buildCarIcon(sHdg, vehicleImageUrlRef.current, 96);
carMarkerRef.current.setIcon({ url: snappedIcon, scaledSize: new g.Size(96, 96), anchor: new g.Point(48, 48) });

carMarkerRef.current.setPosition({ lat: sLat, lng: sLng });
      displayPosRef.current = { lat: sLat, lng: sLng };
      return;
    }

    // Fallback lerp
    carMarkerRef.current.setIcon({ url: iconUrl, scaledSize: new g.Size(96, 96), anchor: new g.Point(48, 48) });
    const start = displayPosRef.current || { lat, lng };
    const dLat = (lat - start.lat) / LERP, dLng = (lng - start.lng) / LERP;
    if (lerpRef.current) clearInterval(lerpRef.current);
    let step = 0;
    lerpRef.current = setInterval(() => {
      step++;
      const la = start.lat + dLat * step, ln = start.lng + dLng * step;
      carMarkerRef.current?.setPosition({ lat: la, lng: ln });
      displayPosRef.current = { lat: la, lng: ln };
      if (step >= LERP) { clearInterval(lerpRef.current!); lerpRef.current = null; }
    }, 16);
  }, [mapReady]);

  /* ── Draw polylines via Directions API (real roads) ── */
  const drawPolylines = useCallback((drLat: number, drLng: number, trip: any) => {
    if (!mapRef.current || !mapReady || !trip) return;
    const g = (window as any).google.maps;

    const origin = { lat: drLat, lng: drLng };
    const dest =
      trip.status === "ongoing" && trip.dropoffLat
        ? { lat: trip.dropoffLat, lng: trip.dropoffLng }
        : trip.pickupLat
        ? { lat: trip.pickupLat,  lng: trip.pickupLng  }
        : null;

    // Update markers
    if (pickupMarkRef.current) { pickupMarkRef.current.setMap(null); pickupMarkRef.current = null; }
    if (dropMarkRef.current)   { dropMarkRef.current.setMap(null);   dropMarkRef.current   = null; }
    if (trip.pickupLat)  pickupMarkRef.current = new g.Marker({ position: { lat: trip.pickupLat,  lng: trip.pickupLng  }, map: mapRef.current, icon: { url: mkPickup(),  scaledSize: new g.Size(28, 28), anchor: new g.Point(14, 14) }, zIndex: 10 });
    if (trip.dropoffLat) dropMarkRef.current   = new g.Marker({ position: { lat: trip.dropoffLat, lng: trip.dropoffLng }, map: mapRef.current, icon: { url: mkDropoff(), scaledSize: new g.Size(34, 44), anchor: new g.Point(17, 44) }, zIndex: 10 });

    if (!dest) { mapRef.current.panTo(origin); return; }

    // Dedup — skip if origin/dest/status haven't changed meaningfully
    const snap = (n: number) => Math.round(n * 10000) / 10000;
    const key  = `${trip.status}|${snap(drLat)},${snap(drLng)}|${dest.lat},${dest.lng}`;
    if (key === lastRouteKeyRef.current) return;
    lastRouteKeyRef.current = key;

    const isOngoing = trip.status === "ongoing";

    // Init renderer once
    if (!dirRendererRef.current) {
      dirRendererRef.current = new g.DirectionsRenderer({
        suppressMarkers: true,
        preserveViewport: true,
        polylineOptions: {
          strokeColor:   isOngoing ? H.green : H.goldDark,
          strokeWeight:  isOngoing ? 5 : 3,
          strokeOpacity: isOngoing ? 0.88 : 0,
          icons: !isOngoing ? [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3.5, strokeColor: H.goldDark }, offset: "0", repeat: "14px" }] : [],
        },
      });
      dirRendererRef.current.setMap(mapRef.current);
    } else {
      dirRendererRef.current.setOptions({
        polylineOptions: {
          strokeColor:   isOngoing ? H.green : H.goldDark,
          strokeWeight:  isOngoing ? 5 : 3,
          strokeOpacity: isOngoing ? 0.88 : 0,
          icons: !isOngoing ? [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3.5, strokeColor: H.goldDark }, offset: "0", repeat: "14px" }] : [],
        },
      });
    }

    new g.DirectionsService().route(
      { origin, destination: dest, travelMode: g.TravelMode.DRIVING, provideRouteAlternatives: false },
      (result: any, status: any) => {
        if (status !== g.DirectionsStatus.OK) {
          // Fallback straight line
          polylinesRef.current.forEach(p => p.setMap(null)); polylinesRef.current = [];
          polylinesRef.current.push(new g.Polyline({
            path: [origin, dest], map: mapRef.current,
            strokeColor: H.goldDark, strokeOpacity: 0, strokeWeight: 0,
            icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3.5, strokeColor: H.goldDark }, offset: "0", repeat: "14px" }],
            zIndex: 5,
          }));
          const b = new g.LatLngBounds(); [origin, dest].forEach(p => b.extend(p));
          mapRef.current.fitBounds(b, { top: 80, bottom: 260, left: 40, right: 400 });
          return;
        }

        dirRendererRef.current.setDirections(result);

        // Store road path for car snapping
        const newPath = result.routes[0].overview_path as any[];
        const destChanged = routePathRef.current.length === 0 ||
          newPath[newPath.length - 1]?.lat() !== routePathRef.current[routePathRef.current.length - 1]?.lat();
        routePathRef.current = newPath;
        if (destChanged) carPctRef.current = 0;

        // Fit bounds on first fetch per dest
        if (destChanged) {
          const b = new g.LatLngBounds();
          result.routes[0].legs[0].steps.forEach((s: any) => { b.extend(s.start_location); b.extend(s.end_location); });
          mapRef.current.fitBounds(b, { top: 80, bottom: 260, left: 40, right: 400 });
        }
      }
    );
  }, [mapReady]);

  /* ── GPS watch ── */
useEffect(() => {
  if (!driverId || !navigator.geolocation) return;
  let firstFix = true;

  watchRef.current = navigator.geolocation.watchPosition(pos => {
    const { latitude: lat, longitude: lng, heading, speed } = pos.coords;
    let h = heading || headingRef.current;
    if (prevGPSRef.current) {
      const d = haversineKm(prevGPSRef.current.lat, prevGPSRef.current.lng, lat, lng);
      if (d > 0.005) { h = bearingDeg(prevGPSRef.current.lat, prevGPSRef.current.lng, lat, lng); headingRef.current = h; }
    }
    prevGPSRef.current = { lat, lng };
    setDriverPos({ lat, lng });
    animateCar(lat, lng, h);

    if (firstFix && mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(17);
      firstFix = false;
      // driverOnline with first GPS fix — lat/lng available here
      fetch(`${API}/driverOnline`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ driverId, lat, lng }),
      }).catch(console.warn);
    }

    if (activeTripRef.current) drawPolylines(lat, lng, activeTripRef.current);

    fetch(`${API}/driverPing`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ driverId, lat, lng, heading: h, speed: speed || 0, isAvailable: !activeTripRef.current }),
    }).catch(console.warn);
  }, e => console.warn("GPS:", e.message), { enableHighAccuracy: true, timeout: 8000, maximumAge: 3000 });

  return () => {
    if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    fetch(`${API}/driverOffline`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ driverId }) }).catch(console.warn);
  };
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [driverId, animateCar, drawPolylines]);

  /* Redraw on trip change */
  useEffect(() => {
    if (!activeTrip) return;
    const pos = displayPosRef.current || driverPos;
    if (pos) drawPolylines(pos.lat, pos.lng, activeTrip);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTrip, drawPolylines]);

  /* Refresh the marker icon in place if the active vehicle changes mid-session
     (e.g. driver switches active car in DriverCars while this tab stays open),
     or remove it entirely if the vehicle just went offline / got deselected. */
  useEffect(() => {
    if (!mapReady) return;
    if (!activeVehicleOnline) {
      if (carMarkerRef.current) { carMarkerRef.current.setMap(null); carMarkerRef.current = null; }
      return;
    }
    if (!carMarkerRef.current) return;
    (async () => {
      const g = (window as any).google.maps;
      const iconUrl = await buildCarIcon(headingRef.current, vehicleImageUrl, 96);
      carMarkerRef.current.setIcon({ url: iconUrl, scaledSize: new g.Size(96, 96), anchor: new g.Point(48, 48) });
    })();
  }, [vehicleImageUrl, mapReady, activeVehicleOnline]);

  /* ── Accept ── */
  const handleAccept = useCallback(async (trip: any) => {
    const tripId = trip.tripId || trip.id;
    if (!driverId || accepting) return;
    setAccepting(tripId);
    try {
      const res = await fetch(`${API}/acceptRide`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tripId, driverId, etaMinutes: trip.etaMinutes || 5 }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error?.message || "Failed");
      await remove(dbRef(rtdb, `driverRequests/${driverId}/${tripId}`));
      subscribeToTrip(tripId);
      setTab("active"); setSheetMode("half");
      toast("Ride accepted! Head to pickup.");
    } catch (e: any) { alert(e.message); }
    finally { setAccepting(null); }
  }, [driverId, accepting, subscribeToTrip]);

  /* ── Decline ── */
  const handleDecline = useCallback(async (trip: any) => {
    const tripId = trip.tripId || trip.id;
    if (!driverId || declining) return;
    setDeclining(tripId);
    try {
      await fetch(`${API}/rejectRide`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tripId, driverId, reason: "driver_declined" }),
      });
      await remove(dbRef(rtdb, `driverRequests/${driverId}/${tripId}`));
    } catch (e: any) { alert(e.message); }
    finally { setDeclining(null); }
  }, [driverId, declining]);

  /* ── Advance trip status ── */
  const handleAdvance = useCallback(async () => {
    if (!activeTrip || !driverId || advancing) return;
    const { tripId, status, distanceKm, pickupLat, pickupLng } = activeTrip;
    setAdvancing(true);
    try {
      if (status === "accepted") {
        const eta = driverPos && pickupLat
          ? Math.max(1, Math.round((haversineKm(driverPos.lat, driverPos.lng, pickupLat, pickupLng) / 40) * 60))
          : 1;
        const res = await fetch(`${API}/driverArriving`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tripId, driverId, etaMinutes: eta }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error?.message || "Failed");
        setSheetMode("full"); toast("Rider notified — you're arriving!");
      } else if (status === "arriving") {
        const res = await fetch(`${API}/startRide`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tripId, driverId }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error?.message || "Failed");
        toast("Ride started! Drive to destination.");
      } else if (status === "ongoing") {
        const res = await fetch(`${API}/completeRide`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tripId, driverId, actualDistanceKm: distanceKm || 0 }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error?.message || "Failed");
      }
    } catch (e: any) { alert(e.message); }
    finally { setAdvancing(false); }
  }, [activeTrip, driverId, advancing, driverPos]);

  /* ── Done (clear active trip) ── */
  const handleDone = useCallback(() => {
    if (activeTripUnsubRef.current) { activeTripUnsubRef.current(); activeTripUnsubRef.current = null; }
    setActiveTrip(null); activeTripRef.current = null; setSheetMode("peek");
    if (pickupMarkRef.current) { pickupMarkRef.current.setMap(null); pickupMarkRef.current = null; }
    if (dropMarkRef.current)   { dropMarkRef.current.setMap(null);   dropMarkRef.current   = null; }
 polylinesRef.current.forEach(p => p.setMap(null)); polylinesRef.current = [];
    if (dirRendererRef.current) { dirRendererRef.current.setMap(null); dirRendererRef.current = null; }
    routePathRef.current = [];
    carPctRef.current = 0;
    lastRouteKeyRef.current = "";
  }, []);

  /* ── Derived ── */
  const trip       = activeTrip;
  const sc         = SC[trip?.status || "accepted"] || SC.accepted;
  const rider      = riderCache[trip?.riderId || ""] || {};
  const isTerminal = trip && ["completed", "cancelled"].includes(trip.status);

  /* ── Active trip panel content ── */
  const ActiveContent = () => {
    if (initializing) return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 0", gap: 12, flexDirection: "column" }}>
        <Loader2 size={28} color={H.red} className="spin" />
        <div style={{ fontSize: 12, color: "var(--muted)" }}>Checking for active trip…</div>
      </div>
    );

    if (!trip) return (
      <div className="rb-idle">
        <div className="rb-idle-orb">
          <div className="rb-idle-ring" />
          {vehicleImageUrl
            ? <img
                src={vehicleImageUrl}
                alt=""
                style={{ width: 34, height: 34, objectFit: "contain" }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
            : <img src={appLogo} alt="" style={{ width: 30, height: 30, objectFit: "contain", borderRadius: 8 }} />}
        </div>
        <div className="rb-idle-title">Ready to Drive</div>
        <div className="rb-idle-sub">Incoming ride requests will appear on the Requests tab. Stay online!</div>
        <div className="rb-pulse-row">
          <div className="rb-pulse" /><div className="rb-pulse" /><div className="rb-pulse" />
        </div>
      </div>
    );

    return (
      <>
        {/* Status banner */}
        <div className="rb-status-banner" style={{ borderColor: sc.color + "33", background: sc.bg }}>
          <div className="rb-status-icon" style={{ background: sc.color + "18" }}>
            <sc.Icon size={20} color={sc.color} />
          </div>
          <div className="rb-status-text">
            <div className="rb-status-label" style={{ color: sc.color }}>{sc.headline}</div>
            <div className="rb-status-sub">{sc.sub}</div>
          </div>
          {trip.etaMinutes && !isTerminal && (
            <div className="rb-eta">
              <div className="rb-eta-num">{trip.etaMinutes}</div>
              <div className="rb-eta-unit">MIN</div>
            </div>
          )}
        </div>

        {/* Rider card */}
        <div className="rb-rider-card">
          <div className="rb-rc-row">
            <div className="rb-rc-av-wrap">
              {rider.photo
                ? <img src={rider.photo} alt={rider.name} className="rb-rc-av" />
                : <div className="rb-rc-av-fb">{rider.initials || "R"}</div>
              }
              <div className="rb-rc-online" />
            </div>
            <div className="rb-rc-info">
              <div className="rb-rc-name">{rider.name || "Rider"}</div>
              {rider.rating && (
                <div className="rb-rc-rating">
                  <Star size={10} fill={H.gold} color={H.gold} /> {rider.rating}
                </div>
              )}
              <div className="rb-rc-sub">{trip.passengers || 1} passenger{(trip.passengers || 1) > 1 ? "s" : ""}</div>
            </div>
          </div>
          <div className="rb-rc-acts">
            {rider.phone && (
              <button className="rb-rc-act ph" onClick={() => window.open(`tel:${rider.phone}`)}>
                <Phone size={13} /> {rider.phone}
              </button>
            )}
            <button className="rb-rc-act ms" onClick={() => { if (trip.riderId) navigate(`/messages/${trip.riderId}`); }}>
              <MessageSquare size={13} /> Message
            </button>
          </div>
        </div>

        {/* Route */}
        <div className="rb-route">
          <div className="rb-route-row">
            <div className="rb-route-dots">
              <div className="rb-route-dot" style={{ borderColor: H.dark, background: H.dark }} />
              <div className="rb-route-vline" />
            </div>
            <div className="rb-route-body">
              <div className="rb-route-lbl">Pickup</div>
              <div className="rb-route-addr">{trip.pickupAddress || "—"}</div>
            </div>
          </div>
          <div className="rb-route-row">
            <div className="rb-route-dots">
              <div className="rb-route-dot" style={{ borderColor: H.red, background: H.red }} />
            </div>
            <div className="rb-route-body">
              <div className="rb-route-lbl">Dropoff</div>
              <div className="rb-route-addr">{trip.dropoffAddress || "—"}</div>
              {trip.distanceKm && <div className="rb-route-km">{trip.distanceKm} km total</div>}
            </div>
          </div>
        </div>

        {/* Driver's earning */}
        <div className="rb-fare">
          <Wallet size={14} color={H.goldDark} />
          <span className="rb-fare-lbl">Your Earning</span>
          <span className="rb-fare-amt">EUR {(trip.driverProfit || 0).toFixed(2)}</span>
        </div>

        {/* Advance button */}
        {sc.next && !isTerminal && (
          <button
            className="rb-advance"
            style={{ background: sc.nextGrad }}
            onClick={handleAdvance}
            disabled={advancing}
          >
            {advancing ? <Loader2 size={14} className="spin" /> : <><sc.Icon size={14} />{sc.nextLabel}</>}
          </button>
        )}

        {/* Terminal */}
        {trip.status === "completed" && (
          <div className="rb-terminal">
            <div className="rb-terminal-icon" style={{ borderColor: "#BEE7CC", background: H.greenSoft }}>
              <CheckCircle size={30} color={H.green} />
            </div>
            <div className="rb-terminal-title">Trip Complete!</div>
            <div className="rb-terminal-earn">
              Earned <strong style={{ color: H.green }}>EUR {(trip.driverProfit || 0).toFixed(2)}</strong>
              {sessionEarns > 0 && <><br />Session total: <strong style={{ color: H.goldDark }}>EUR {sessionEarns.toFixed(2)}</strong></>}
            </div>
            <button className="rb-done-btn" onClick={handleDone}>DONE</button>
          </div>
        )}
        {trip.status === "cancelled" && (
          <div className="rb-terminal">
            <div className="rb-terminal-icon" style={{ borderColor: "#F3B9BE", background: H.redSoft }}>
              <XCircle size={30} color={H.red} />
            </div>
            <div className="rb-terminal-title">Ride Cancelled</div>
            <div className="rb-terminal-earn">The rider cancelled this trip.</div>
            <button className="rb-done-btn" onClick={handleDone}>DISMISS</button>
          </div>
        )}
      </>
    );
  };

  /* ── Requests panel ── */
  const RequestsPanel = () => (
    <div className="rb-req-panel">
      <div className="rb-req-inner">
        <div className="rb-req-hdr">
          <div>
            <div className="rb-req-title">Requests</div>
            <div className="rb-req-sub">{requestedTrips.length} pending</div>
          </div>
          {requestedTrips.length > 0 && (
            <div className="rb-req-badge"><Bell size={10} />{requestedTrips.length}</div>
          )}
        </div>

        {requestedTrips.length === 0 ? (
          <div className="rb-empty">
            <Bell size={52} color="var(--bord)" style={{ marginBottom: 8 }} />
            <div className="rb-empty-title">No Requests</div>
            <div className="rb-empty-sub">Ride requests assigned to you appear here. Stay online to receive trips!</div>
          </div>
        ) : (
          requestedTrips.map((trip, i) => {
            const tKey  = trip.tripId || trip.id;
            const cd    = countdowns[tKey] ?? REQUEST_TIMEOUT;
            const pct   = cd / REQUEST_TIMEOUT;
            const low   = cd < 30;
            const isExp = expiredIds.has(tKey);
            const r     = riderCache[trip.riderId || ""] || {};
            const isAcc = accepting === tKey;
            const isDec = declining === tKey;
            const circumference = 2 * Math.PI * 20; // r=20
            const dashOffset = circumference * (1 - pct);
            const timerColor = cd < 15 ? H.red : cd < 45 ? H.amber : H.green;

            return (
              <div
                key={tKey}
                className={`req-card${isExp ? " expired" : ""}`}
                style={{ "--cd": `${i * 0.055}s` } as any}
              >
                {/* Accent bar */}
                <div className="req-accent" style={{ background: isExp ? "#ccc" : (low ? H.red : H.green) }} />

                <div className="req-body">
                  {/* Top: badge + circular countdown */}
                  <div className="req-top">
                    <div className="req-pill" style={{
                      borderColor: isExp ? "#ddd" : (low ? "#F3B9BE" : "#BEE7CC"),
                      background:  isExp ? "#f5f5f5" : (low ? H.redSoft : H.greenSoft),
                      color:       isExp ? "#aaa"    : (low ? H.red : "#15803d"),
                    }}>
                      <Car size={9} />
                      {isExp ? "EXPIRED" : "RIDE REQUEST"}
                    </div>

                    {/* Circular countdown timer */}
                    <div className="req-timer-wrap">
                      <svg className="req-timer-svg" viewBox="0 0 48 48">
                        <circle className="req-timer-bg" cx="24" cy="24" r="20" />
                        <circle
                          className="req-timer-fill"
                          cx="24" cy="24" r="20"
                          stroke={isExp ? "#ccc" : timerColor}
                          strokeDasharray={circumference}
                          strokeDashoffset={isExp ? circumference : dashOffset}
                        />
                      </svg>
                      <div className="req-timer-center">
                        <div className="req-timer-num" style={{ color: isExp ? "#aaa" : timerColor }}>
                          {isExp ? "–" : cd}
                        </div>
                        <div className="req-timer-lbl">SEC</div>
                      </div>
                    </div>
                  </div>

                  {/* Rider info */}
                  <div className="req-rider">
                    {r.photo
                      ? <img src={r.photo} alt={r.name} className="req-av" />
                      : <div className="req-av-fb">{r.initials || <Users size={16} />}</div>
                    }
                    <div className="req-rider-info">
                      <div className="req-rider-name">{r.name || "Loading…"}</div>
                      {r.rating && (
                        <div className="req-rider-rating">
                          <Star size={9} fill={H.goldDark} color={H.goldDark} /> {r.rating}
                        </div>
                      )}
                      <div className="req-rider-sub">{trip.passengers || 1} passenger{(trip.passengers || 1) > 1 ? "s" : ""}</div>
                    </div>
                    <div className="req-contacts">
                      <button
                        className="req-cbtn ph"
                        onClick={() => { if (r.phone) window.open(`tel:${r.phone}`); }}
                        title={r.phone || "No phone"}
                      >
                        <Phone size={13} />
                      </button>
                      <button
                        className="req-cbtn ms"
                        onClick={() => { if (trip.riderId) navigate(`/messages/${trip.riderId}`); }}
                      >
                        <MessageSquare size={12} />
                      </button>
                    </div>
                  </div>

                  {/* Route */}
                  <div className="req-route">
                    <div className="req-route-row">
                      <div className="req-route-dot" style={{ background: H.dark }} />
                      <div className="req-route-body">
                        <div className="req-route-lbl">Pickup</div>
                        <div className="req-route-addr">{trip.pickupAddress || "Not specified"}</div>
                      </div>
                    </div>
                    <div style={{ width: 1, height: 10, background: "var(--bord)", margin: "3px 0 3px 3.5px" }} />
                    <div className="req-route-row">
                      <div className="req-route-dot" style={{ background: H.red }} />
                      <div className="req-route-body">
                        <div className="req-route-lbl">Dropoff</div>
                        <div className="req-route-addr">{trip.dropoffAddress || "Not specified"}</div>
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="req-stats">
                    {trip.rideDistanceKm != null && (
                      <span className="req-stat"><Navigation size={10} color={H.blue} />{Number(trip.rideDistanceKm || trip.distanceKm || 0).toFixed(1)} km trip</span>
                    )}
                    {trip.driverToPickupKm != null && (
                      <span className="req-stat"><MapPin size={10} color={H.amber} />{Number(trip.driverToPickupKm).toFixed(1)} km away</span>
                    )}
                    {trip.etaMinutes && (
                      <span className="req-stat"><Clock size={10} color={H.amber} />{trip.etaMinutes} min ETA</span>
                    )}
                    {trip.totalCharged != null && (
                      <span className="req-stat"><Wallet size={10} color={H.goldDark} />EUR {Number(trip.totalCharged).toFixed(2)}</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="req-acts">
                    <button
                      className="req-dec"
                      onClick={() => handleDecline(trip)}
                      disabled={isDec || isAcc || isExp}
                    >
                      {isDec ? <Loader2 size={12} className="spin" /> : <><XCircle size={12} />DECLINE</>}
                    </button>
                    <button
                      className="req-acc"
                      onClick={() => handleAccept(trip)}
                      disabled={isAcc || isDec || isExp}
                    >
                      {isAcc ? <Loader2 size={12} className="spin" /> : <><CheckCircle size={12} />ACCEPT</>}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  /* ── Render ─────────────────────────────────────────────────────────── */
  return (
    <div className="rb">
      {/* Header — no standalone logo/brand block; this lives inside the dashboard shell */}
      <header className="rb-hdr">
        <span className="rb-hdr-title">
          {tab === "active" ? (trip ? sc.label : "Driver Dashboard") : "Requests"}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
          {sessionEarns > 0 && (
            <div className="rb-earn-pill">
              <TrendingUp size={11} color={H.goldDark} />
              <span className="rb-earn-val">EUR {sessionEarns.toFixed(2)}</span>
            </div>
          )}
          <div className="rb-online-pill">
            <div className="rb-online-dot" />
            <span className="rb-online-txt">ONLINE</span>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="rb-tabbar">
        <button className={`rb-tab${tab === "active" ? " rb-tab-on" : ""}`} onClick={() => setTab("active")}>
          <Car size={12} /> Active
          {trip && ACTIVE_STATUSES.includes(trip.status) && <div className="rb-tab-pip" />}
        </button>
        <button className={`rb-tab${tab === "requests" ? " rb-tab-on" : ""}`} onClick={() => setTab("requests")}>
          <Bell size={12} /> Requests
          {requestedTrips.length > 0 && <div className="rb-tab-pip" />}
        </button>
      </div>

      {/* Body — map is always rendered */}
      <div style={{ position: "absolute", inset: "96px 0 0 0", overflow: "hidden" }}>
        {/* Map */}
        {mapReady
          ? <div id="rb-gmap" style={{ position: "absolute", inset: 0 }} />
          : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--off)" }}><Loader2 size={28} color={H.red} className="spin" /></div>
        }

        {/* Requests panel */}
        {tab === "requests" && <RequestsPanel />}

        {/* Active trip — desktop sidebar */}
        {tab === "active" && (
          <div className="rb-sidebar">
            <div className="rb-sb-status">
              <div className="rb-sb-status-row">
                <div className="rb-sb-icon" style={{ background: trip ? sc.bg : "var(--off)" }}>
                  {trip ? <sc.Icon size={20} color={sc.color} /> : <Car size={20} color="var(--muted)" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--serif)", fontSize: 17, fontWeight: 800, color: "var(--blk)", lineHeight: 1.2 }}>
                    {trip ? sc.headline : "Ready to Drive"}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--smoke)", marginTop: 2 }}>
                    {trip ? sc.sub : "Waiting for ride requests…"}
                  </div>
                </div>
                {trip?.etaMinutes && !isTerminal && (
                  <div className="rb-eta" style={{ padding: "7px 12px" }}>
                    <div className="rb-eta-num" style={{ fontSize: 20 }}>{trip.etaMinutes}</div>
                    <div className="rb-eta-unit">MIN</div>
                  </div>
                )}
              </div>
            </div>
            <div className="rb-sb-body"><ActiveContent /></div>
          </div>
        )}

        {/* Active trip — mobile: same full-panel layout as Requests, not a
            draggable peek/half/full sheet. */}
        {tab === "active" && (
          <div className="rb-active-panel">
            <div className="rb-req-inner">
              <div className="rb-req-hdr">
                <div>
                  <div className="rb-req-title">{trip ? sc.label : "Active"}</div>
                  <div className="rb-req-sub">{trip ? sc.sub : "Waiting for ride requests…"}</div>
                </div>
                {trip?.etaMinutes && !isTerminal && (
                  <div className="rb-eta" style={{ padding: "7px 12px" }}>
                    <div className="rb-eta-num" style={{ fontSize: 20 }}>{trip.etaMinutes}</div>
                    <div className="rb-eta-unit">MIN</div>
                  </div>
                )}
              </div>
              <ActiveContent />
            </div>
          </div>
        )}
      </div>

      {/* ── Expired request modal ── */}
      {expiredModal && (
        <div className="rb-modal-overlay" onClick={() => setExpiredModal(null)}>
          <div className="rb-modal" onClick={e => e.stopPropagation()}>
            <div className="rb-modal-icon">
              <AlertTriangle size={28} />
            </div>
            <div className="rb-modal-title">Request Expired</div>
            <div className="rb-modal-sub">
              The 2-minute window for <strong>{expiredModal.riderName}'s</strong> ride has closed.
              The system is automatically searching for the next available driver.
            </div>
            <button className="rb-modal-btn" onClick={() => setExpiredModal(null)}>
              GOT IT
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      <div className={`rb-toast${showToast ? " show" : ""}`}>
        <CheckCircle size={14} color={H.green} />{toastMsg}
      </div>
    </div>
  );
}